import type { ComponentChildren } from "preact";
import { useEffect, useState } from "preact/hooks";
import { ApiError, api } from "../../api/client.ts";
import type {
  Field as FieldDef,
  Issue,
  ItemDetail,
  Link as Linked,
  Member,
  ObjEntity,
  SpaceOut,
} from "../../api/types.gen.ts";
import { type Key, SLOT, t } from "../../i18n/index.ts";
import { ATTACHMENTS, attachFiles } from "../../lib/attach.ts";
import { useEntities } from "../../lib/data.ts";
import { mergePatch } from "../../lib/diff.ts";
import { type Draft, draftOf, rebase } from "../../lib/draft.ts";
import { linkId, withIds } from "../../lib/links.ts";
import { spaceApi, spaceUrl } from "../../lib/paths.ts";
import { reviewerName } from "../../lib/people.ts";
import { type Resource, useAction } from "../../lib/resource.ts";
import { canEdit } from "../../lib/roles.ts";
import { issueKey } from "../../lib/state.ts";
import { toast } from "../../lib/toast.ts";
import { Button, Link } from "../../ui/Button.tsx";
import { Field, Form, Input } from "../../ui/form.tsx";
import { Async, Section, Slotted } from "../../ui/misc.tsx";
import { FORM_ROW } from "../../ui/styles.ts";
import { EntitySelect } from "../items/entitySelect.tsx";
import { LinkTable } from "./linkTable.tsx";
import { RefInput, ValueInput } from "./valueInputs.tsx";

const KEY = /^[A-Za-z_][A-Za-z0-9_]*$/u;
/** Types edited with several controls (amount + currency, wall time + zone): labelled as a group. */
const GROUPED = new Set<FieldDef["type"]>(["money", "datetime"]);

/** The PATCH body turning `item` into the draft, or null when nothing changed. */
function patchOf(item: ItemDetail, d: Draft): Record<string, unknown> | null {
  const p: Record<string, unknown> = {};
  if (d.title !== (item.title ?? "")) {
    p["title"] = d.title;
  }
  if (d.entity !== (item.entity ?? "")) {
    p["entity"] = d.entity || null;
  }
  const data = mergePatch(item.data ?? {}, d.data);
  const refs = mergePatch(item.refs ?? {}, d.refs);
  if (data) {
    p["data"] = data;
  }
  if (refs) {
    p["refs"] = refs;
  }
  return Object.keys(p).length > 0 ? p : null;
}

/**
 * The issues about a data or refs path (`data.total`, `data.tags[0]`, `refs.customer`), one per line; a reviewer's
 * finding ends with their name, linking to the review. Null when there are none.
 */
function issuesAt(p: EditorProps, path: string, exact = false): ComponentChildren | null {
  const at = p.item.issues.filter(
    (i) => i.path === path || (!exact && (i.path?.startsWith(`${path}[`) || i.path?.startsWith(`${path}.`))),
  );
  return at.length === 0 ? null : at.map((i) => <IssueLine key={issueKey(i)} {...p} issue={i} />);
}

function IssueLine({ space, members, issue: i }: EditorProps & { issue: Issue }) {
  return (
    <span class="block">
      {i.review && i.reviewer ? (
        <Slotted text={t("item.issueBy", { message: i.message, name: SLOT })}>
          <Link href={spaceUrl(space, `/reviews/${i.review}`)} class="font-medium underline hover:no-underline">
            {reviewerName(members, i)}
          </Link>
        </Slotted>
      ) : (
        i.message
      )}
    </span>
  );
}

function FieldLabel({ f }: { f: FieldDef }) {
  return (
    <>
      {f.label || f.key}
      {f.required ? <span class="text-red-700"> *</span> : null}
    </>
  );
}

function AddKey({ onAdd, label }: { onAdd: (key: string) => void; label: Key }) {
  const [key, setKey] = useState("");
  return (
    <div class={FORM_ROW}>
      <Field label={t(label)}>
        <Input class="font-mono" value={key} onValue={setKey} />
      </Field>
      <Button
        disabled={!KEY.test(key)}
        onClick={() => {
          onAdd(key);
          setKey("");
        }}
      >
        {t("common.add")}
      </Button>
    </div>
  );
}

interface EditorProps {
  space: SpaceOut;
  item: ItemDetail;
  members: readonly Member[] | undefined;
  draft: Draft;
  fields: readonly FieldDef[];
  setData: (key: string, v: unknown) => void;
  setRefs: (key: string, links: Linked[]) => void;
  /** Uploads files straight into a relationship (saved on the server right away). */
  onAttach: (key: string, files: File[]) => Promise<void>;
}

/**
 * A ref field's editor: a table of links when they carry values (link fields), else chips. One open to any item (no
 * target entity) is where documents go, so it offers uploads.
 */
function RefField(p: EditorProps & { f: FieldDef }) {
  const { f, space, item, draft, setRefs, onAttach } = p;
  const links = draft.refs[f.key] ?? [];
  if (f.fields?.length) {
    return (
      <Field group={true} label={<FieldLabel f={f} />} error={issuesAt(p, `refs.${f.key}`, true)}>
        <LinkTable
          space={space}
          field={f}
          links={links}
          briefs={item.links[f.key] ?? []}
          issues={item.issues}
          renderIssue={(i) => <IssueLine {...p} issue={i} />}
          onChange={(next) => setRefs(f.key, next)}
        />
      </Field>
    );
  }
  return (
    <Field group={true} labelHidden={true} label={<FieldLabel f={f} />} error={issuesAt(p, `refs.${f.key}`)}>
      <RefInput
        space={space}
        label={f.label || f.key}
        heading={<FieldLabel f={f} />}
        ids={links.map(linkId)}
        links={item.links[f.key] ?? []}
        target={f.target ?? null}
        many={f.many ?? false}
        onChange={(ids) => setRefs(f.key, withIds(links, ids))}
        onUpload={f.target ? undefined : (files) => onAttach(f.key, files)}
      />
    </Field>
  );
}

/** One editor per schema field, with its issues underneath. */
function SchemaFields(p: EditorProps) {
  const { space, draft, fields, setData } = p;
  return (
    <>
      {fields.map((f) =>
        f.type === "ref" ? (
          <RefField key={f.key} {...p} f={f} />
        ) : (
          <Field
            key={f.key}
            group={Boolean(f.many) || GROUPED.has(f.type)}
            label={<FieldLabel f={f} />}
            error={issuesAt(p, `data.${f.key}`)}
            hint={f.description}
          >
            <ValueInput
              field={{ ...f, time_zone: f.time_zone ?? space.time_zone, region: f.region ?? space.region ?? null }}
              label={GROUPED.has(f.type) ? f.label || f.key : undefined}
              value={draft.data[f.key]}
              onChange={(v) => setData(f.key, v)}
            />
          </Field>
        ),
      )}
    </>
  );
}

/** Data keys outside the schema (all keys of an item without entity), editable as JSON and removable. */
function ExtraData(p: EditorProps & { hasSchema: boolean }) {
  const { draft, fields, setData, hasSchema } = p;
  const editable = canEdit(p.space);
  const extras = Object.keys(draft.data).filter((k) => !fields.some((f) => f.key === k && f.type !== "ref"));
  return (
    <>
      {extras.length > 0 ? (
        <div class="flex flex-col gap-3 rounded-md border border-amber-200 bg-amber-50/50 p-3">
          <p class="text-sm text-amber-900">{t(hasSchema ? "item.extraHint" : "item.dataHint")}</p>
          {extras.map((k) => (
            <div key={k} class="flex items-start gap-2">
              <div class="flex-1">
                <Field label={<code>{k}</code>} error={issuesAt(p, `data.${k}`)}>
                  <ValueInput field={{ key: k, type: "any" }} value={draft.data[k]} onChange={(v) => setData(k, v)} />
                </Field>
              </div>
              {editable ? (
                <Button size="sm" variant="ghost" class="mt-6" onClick={() => setData(k, undefined)}>
                  {t("common.remove")}
                </Button>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
      {editable ? <AddKey label="item.addDataKey" onAdd={(k) => setData(k, draft.data[k] ?? "")} /> : null}
    </>
  );
}

/** Relationships outside the schema's ref fields (attachments have their own section). */
function Links({ space, item, draft, fields, setRefs }: EditorProps) {
  const keys = Object.keys(draft.refs).filter(
    (k) => k !== ATTACHMENTS && !fields.some((f) => f.key === k && f.type === "ref"),
  );
  const editable = canEdit(space);
  if (!editable && keys.length === 0) {
    return null;
  }
  return (
    <Section title={t("item.links")}>
      <div class="flex flex-col gap-4">
        {keys.map((k) => (
          <Field key={k} group={true} labelHidden={true} label={<code>{k}</code>}>
            <RefInput
              space={space}
              label={k}
              heading={<code>{k}</code>}
              ids={(draft.refs[k] ?? []).map(linkId)}
              links={item.links[k] ?? []}
              many={true}
              onChange={(ids) => setRefs(k, withIds(draft.refs[k] ?? [], ids))}
            />
          </Field>
        ))}
        {editable ? <AddKey label="item.addLinkKey" onAdd={(k) => setRefs(k, draft.refs[k] ?? [])} /> : null}
      </div>
    </Section>
  );
}

/**
 * Draft state based on a version of the item. New server versions replace it while there are no local edits, and
 * otherwise are merged in when they changed other things (see `rebase`); true conflicts wait for the user.
 */
function useDraft(item: ItemDetail) {
  const [base, setBase] = useState(item);
  const [draft, setDraft] = useState(() => draftOf(item));
  const [gen, setGen] = useState(0);
  const reset = (i: ItemDetail) => {
    setBase(i);
    setDraft(draftOf(i));
    setGen((g) => g + 1);
  };
  const patch = patchOf(base, draft);
  // biome-ignore lint/correctness/useExhaustiveDependencies: only a new server version matters here.
  useEffect(() => {
    if (item.ver === base.ver) {
      return;
    }
    if (patch === null) {
      reset(item);
      return;
    }
    // Merged in when it changed other things; else it waits (a banner says so).
    const next = rebase(draftOf(base), draft, draftOf(item));
    if (next) {
      setBase(item);
      setDraft(next);
    }
  }, [item.ver]);
  return { base, draft, setDraft, gen, reset, patch };
}

interface FormProps {
  space: SpaceOut;
  item: ItemDetail;
  entities: readonly ObjEntity[];
  /** The space's members (the page has them already), for reviewer names. */
  members: readonly Member[] | undefined;
  onSaved: (i: ItemDetail) => void;
  onReload: () => void;
}

function ItemForm({ space, item, entities, members, onSaved, onReload }: FormProps) {
  const { base, draft, setDraft, gen, reset, patch } = useDraft(item);
  const [save, saving] = useAction(async () => {
    try {
      const next = await api.patch<ItemDetail>(spaceApi(space, `/items/${item.id}`), { ...patch, ver: base.ver });
      reset(next);
      onSaved(next);
      toast(t("item.saved"));
    } catch (e) {
      if (e instanceof ApiError && e.code === "version_conflict") {
        onReload();
      }
      throw e;
    }
  });
  const schema = entities.find((e) => e.id === draft.entity) ?? (draft.entity === base.entity ? base.schema : null);
  const props: EditorProps = {
    space,
    item,
    members,
    draft,
    fields: schema?.fields ?? [],
    setData: (k, v) => setDraft({ ...draft, data: { ...draft.data, [k]: v } }),
    setRefs: (k, ids) => setDraft({ ...draft, refs: { ...draft.refs, [k]: ids } }),
    onAttach: async (k, files) => {
      await attachFiles(space, item.id, files, k);
      toast(t("attachments.added", { count: files.length }));
      onReload();
    },
  };
  const readOnly = !canEdit(space);
  return (
    <Form onSubmit={save}>
      {item.ver === base.ver ? null : (
        <div
          role="alert"
          class="mb-4 flex items-center justify-between gap-3 rounded-md bg-amber-50 px-4 py-3 text-sm text-amber-900"
        >
          <span>{t("item.changedElsewhere")}</span>
          <Button size="sm" onClick={() => reset(item)}>
            {t("item.discardAndReload")}
          </Button>
        </div>
      )}
      <fieldset key={gen} disabled={readOnly} class="flex min-w-0 flex-col gap-4">
        <div class="grid gap-4 sm:grid-cols-2">
          <Field label={t("items.title")}>
            <Input value={draft.title} onValue={(v) => setDraft({ ...draft, title: v })} />
          </Field>
          <Field label={t("items.entity")}>
            <EntitySelect entities={entities} value={draft.entity} onValue={(v) => setDraft({ ...draft, entity: v })} />
          </Field>
        </div>
        <SchemaFields {...props} />
        <ExtraData {...props} hasSchema={Boolean(schema)} />
        <Links {...props} />
      </fieldset>
      {readOnly ? null : (
        <div class="sticky bottom-0 mt-4 flex justify-end gap-2 border-t border-zinc-200 bg-zinc-50 py-3">
          <Button disabled={patch === null} onClick={() => reset(base)}>
            {t("item.discard")}
          </Button>
          <Button variant="primary" submit={true} busy={saving} disabled={patch === null}>
            {t("common.save")}
          </Button>
        </div>
      )}
    </Form>
  );
}

/** The item's form, once the space's entities (what it can be) are in; saving shows the item as saved. */
export function ItemFields({
  space,
  r,
  item,
  members,
}: {
  space: SpaceOut;
  r: Resource<ItemDetail>;
  item: ItemDetail;
  members: readonly Member[] | undefined;
}) {
  const entities = useEntities(space);
  return (
    <Async r={entities}>
      {(list) => (
        <ItemForm space={space} item={item} entities={list} members={members} onSaved={r.set} onReload={r.reload} />
      )}
    </Async>
  );
}
